/**
 * apps/automations-portal/src/features/admin/ClientDetailPage.tsx
 *
 * Client header + deployment cards. Deployment data may 501 (not built
 * server-side yet) — handled as a "coming soon" empty state rather than a
 * crash, while the client header itself (an implemented route) renders
 * normally.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';
import { ApiError, adminApi, isNotImplemented } from '@/lib/api';
import { CARE_PLAN_LABEL, CLIENT_STATUS_LABEL } from '@/lib/types';
import type { Block, CarePlan, ClientDetail, ClientStatus, Deployment } from '@/lib/types';
import { DeployBlockWizard } from './DeployBlockWizard';
import { DeploymentCard } from './DeploymentCard';
import { InboundAddressPanel } from './InboundAddressPanel';

const STATUS_VARIANT: Record<ClientStatus, BadgeProps['variant']> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  OFFBOARDED: 'outline',
};



export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);
  const [deployments, setDeployments] = useState<Deployment[] | null>(null);
  const [deploymentsState, setDeploymentsState] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [blocks, setBlocks] = useState<Block[]>([]);

  const blocksById = useMemo(() => {
    const map: Record<string, Block> = {};
    for (const block of blocks) map[block.id] = block;
    return map;
  }, [blocks]);

  // Deployments come back embedded in the client detail response, so this is
  // one request rather than two — and the two can never disagree about which
  // client's automations are on screen.
  function loadDeployments(clientId: string) {
    setDeploymentsState('loading');
    adminApi
      .getClient(clientId)
      .then((data) => {
        setClient(data);
        setDeployments(data.deployments ?? []);
        setDeploymentsState('ready');
      })
      .catch((err) => {
        setDeploymentsState(isNotImplemented(err) ? 'unavailable' : 'error');
      });
  }

  /**
   * Deletes a deployment record. Shared by both the missingInEngine "Remove"
   * shortcut and the full confirmed delete — the only difference is whether
   * a failure is treated as an expected no-op (the engine already lost this
   * flow, which is exactly why the missingInEngine path exists) or a real
   * error worth surfacing.
   */
  async function removeDeployment(deployment: Deployment, options: { silent?: boolean } = {}) {
    const { silent = false } = options;
    try {
      await adminApi.deleteDeployment(deployment.id);
      if (silent) {
        toast('Removed', { description: 'The automation record has been deleted.' });
      } else {
        toast('Deleted', { description: `${deployment.blockName} has been removed from this client.` });
      }
    } catch (err) {
      if (silent) {
        // Deleting the flow will fail when it is already gone from the engine —
        // which is exactly the case this button exists for — so the record is
        // cleared regardless and the list refreshed either way.
        toast('Removed the record', {
          description: 'The engine had already lost this automation.',
        });
      } else {
        toast('Could not delete', {
          description: err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
        });
      }
    } finally {
      if (id) loadDeployments(id);
    }
  }

  useEffect(() => {
    if (!id) return;
    adminApi
      .getClient(id)
      .then((data) => {
        setClient(data);
        setDeployments(data.deployments ?? []);
        setDeploymentsState('ready');
      })
      .catch((err) => {
        setClientError('Could not load this client.');
        setDeploymentsState(isNotImplemented(err) ? 'unavailable' : 'error');
      });
  }, [id]);

  useEffect(() => {
    // The block catalogue drives Edit configuration's field schema. Not
    // implemented yet is a normal outcome here too — deployment cards fall
    // back to an "unavailable" message for editing rather than crashing.
    adminApi
      .listBlocks()
      .then(setBlocks)
      .catch(() => setBlocks([]));
  }, []);

  if (!id) return null;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 p-8">
      <Link to="/admin" className="flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        All clients
      </Link>

      {clientError && <p className="text-sm text-destructive">{clientError}</p>}

      {!clientError && !client && <Skeleton className="h-20 w-full" />}

      {client && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{client.name}</h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant={STATUS_VARIANT[client.status]}>{client.status}</Badge>
              <span className="text-sm text-muted-foreground">{client.carePlan} plan</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setEditClientOpen(true)}>
              <Pencil className="h-4 w-4" />
              Edit client
            </Button>
            <Button type="button" onClick={() => setWizardOpen(true)}>
              <Rocket className="h-4 w-4" />
              Deploy a block
            </Button>
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">Deployments</h2>

        {deploymentsState === 'loading' && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {deploymentsState === 'error' && (
          <p className="text-sm text-destructive">Could not load deployments. Try refreshing the page.</p>
        )}

        {deploymentsState === 'unavailable' && (
          <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
            Deployment tracking is coming soon. Once the automation engine reports back, deployed blocks for this
            client will appear here.
          </div>
        )}

        {deploymentsState === 'ready' && deployments?.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
            Nothing deployed for this client yet.
          </div>
        )}

        {deploymentsState === 'ready' && deployments && deployments.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {deployments.map((deployment) => (
              <DeploymentCard
                key={deployment.id}
                deployment={deployment}
                block={blocksById[deployment.blockId]}
                onRefetch={() => loadDeployments(id)}
                onRemoveMissing={() => void removeDeployment(deployment, { silent: true })}
                onDelete={() => removeDeployment(deployment)}
              />
            ))}
          </div>
        )}
      </div>

      {deploymentsState === 'ready' && deployments && (
        <InboundAddressPanel clientId={id} deployments={deployments} />
      )}

      <DeployBlockWizard
        clientId={id}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onDeployed={() => loadDeployments(id)}
      />

      {client && (
        <EditClientDialog
          client={client}
          open={editClientOpen}
          onOpenChange={setEditClientOpen}
          onSaved={setClient}
        />
      )}
    </div>
  );
}

function EditClientDialog({
  client,
  open,
  onOpenChange,
  onSaved,
}: {
  client: ClientDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (client: ClientDetail) => void;
}) {
  const [name, setName] = useState(client.name);
  const [status, setStatus] = useState<ClientStatus>(client.status);
  const [carePlan, setCarePlan] = useState<CarePlan>(client.carePlan);
  const [notes, setNotes] = useState(client.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(client.name);
    setStatus(client.status);
    setCarePlan(client.carePlan);
    setNotes(client.notes ?? '');
    setError(null);
  }, [open, client]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await adminApi.updateClient(client.id, {
        name: name.trim(),
        status,
        carePlan,
        notes: notes.trim(),
      });
      onSaved(updated);
      toast('Client updated', { description: `${updated.name}'s details have been saved.` });
      onOpenChange(false);
    } catch (err) {
      setError('Could not save these changes. Try again.');
      toast('Could not save', {
        description: err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit client</DialogTitle>
          <DialogDescription>Update {client.name}&apos;s details.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-client-name">Business name</Label>
            <Input id="edit-client-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-client-status">Status</Label>
            <Select value={status} onValueChange={(next) => setStatus(next as ClientStatus)}>
              <SelectTrigger id="edit-client-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CLIENT_STATUS_LABEL) as ClientStatus[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {CLIENT_STATUS_LABEL[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-client-care-plan">Care plan</Label>
            <Select value={carePlan} onValueChange={(next) => setCarePlan(next as CarePlan)}>
              <SelectTrigger id="edit-client-care-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CARE_PLAN_LABEL) as CarePlan[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {CARE_PLAN_LABEL[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-client-notes">Notes</Label>
            <Textarea id="edit-client-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
